import axios from 'axios';
import { header } from './header.encry';

const API_URL = process.env.REACT_APP_PEGASUS_API_URL;
const IMAGE_MAX_DIMENSION = 1280;
const IMAGE_MAX_BYTES = 900 * 1024;
const IMAGE_OUTPUT_TYPE = 'image/jpeg';
const IMAGE_INITIAL_QUALITY = 0.82;
const IMAGE_MIN_QUALITY = 0.55;

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('Unable to convert file to base64'));
    };
    reader.onerror = () => reject(reader.error || new Error('FileReader error'));
    reader.readAsDataURL(file);
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
        return;
      }
      reject(new Error('Unable to export canvas'));
    }, type, quality);
  });

const loadImageFromBlob = (blob) =>
  new Promise((resolve, reject) => {
    const imageUrl = URL.createObjectURL(blob);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(imageUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      reject(new Error('Unable to load image for compression'));
    };

    image.src = imageUrl;
  });

const compressImageBlob = async (blob) => {
  if (
    typeof document === 'undefined' ||
    !blob ||
    typeof blob.type !== 'string' ||
    !blob.type.startsWith('image/')
  ) {
    return blob;
  }

  const image = await loadImageFromBlob(blob);
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;

  if (!width || !height) {
    return blob;
  }

  const ratio = Math.min(1, IMAGE_MAX_DIMENSION / Math.max(width, height));
  const targetWidth = Math.max(1, Math.round(width * ratio));
  const targetHeight = Math.max(1, Math.round(height * ratio));
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const context = canvas.getContext('2d');
  if (!context) {
    return blob;
  }

  context.drawImage(image, 0, 0, targetWidth, targetHeight);

  let quality = IMAGE_INITIAL_QUALITY;
  let bestCandidate = await canvasToBlob(canvas, IMAGE_OUTPUT_TYPE, quality);

  while (bestCandidate.size > IMAGE_MAX_BYTES && quality > IMAGE_MIN_QUALITY) {
    quality = Math.max(IMAGE_MIN_QUALITY, quality - 0.08);
    bestCandidate = await canvasToBlob(canvas, IMAGE_OUTPUT_TYPE, quality);
  }

  return bestCandidate;
};

const normalizeImageSource = async (src) => {
  if (!src) {
    return src;
  }

  if (typeof src === 'string') {
    if (src.startsWith('data:image/')) {
      const srcBlob = dataUrlToBlob(src);
      if (srcBlob) {
        const optimizedBlob = await compressImageBlob(srcBlob);
        return fileToDataUrl(optimizedBlob);
      }
    }
    return src;
  }

  if (typeof Blob !== 'undefined' && src instanceof Blob) {
    const optimizedBlob = await compressImageBlob(src);
    return fileToDataUrl(optimizedBlob);
  }

  return src;
};

const dataUrlToBlob = (dataUrl) => {
  if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) {
    return null;
  }

  const [meta, base64Data] = dataUrl.split(',');
  if (!meta || !base64Data) {
    return null;
  }

  const mimeMatch = meta.match(/data:(.*?);base64/);
  const mimeType = mimeMatch?.[1] || 'image/jpeg';
  const binary = atob(base64Data);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new Blob([bytes], { type: mimeType });
};

export const listImageProcessing = async (params = {}) => {
  const res = await axios.get(`${API_URL}/image-processing`, {
    ...header(),
    params,
  });
  return res.data;
};

export const getImageProcessing = async (id) => {
  const res = await axios.get(`${API_URL}/image-processing/${id}`, header());
  return res.data;
};

export const createImageProcessing = async (data) => {
  const payload = {
    ...data,
    src: await normalizeImageSource(data?.src),
  };
  const res = await axios.post(
    `${API_URL}/image-processing`,
    payload,
    header({ 'Content-Type': 'application/json' })
  );
  return res.data;
};

export const updateImageProcessing = async (id, data) => {
  const res = await axios.put(`${API_URL}/image-processing/${id}`, data, header());
  return res.data;
};

export const resetImageProcessingText = async (id) => {
  const res = await axios.post(`${API_URL}/image-processing/${id}/reset-text`, {}, header());
  return res.data;
};

export const uploadProcessedImage = async (id, srcProcessed) => {
  const formData = new FormData();
  const processedBlob =
    typeof Blob !== 'undefined' && srcProcessed instanceof Blob
      ? srcProcessed
      : dataUrlToBlob(srcProcessed);

  if (!processedBlob) {
    throw new Error('src_processed must be a Blob or base64 data URL');
  }

  formData.append('src_processed', processedBlob, `processed-${id}.jpg`);

  const res = await axios.post(
    `${API_URL}/image-processing/${id}/processed`,
    formData,
    header({ 'Content-Type': 'multipart/form-data' })
  );
  return res.data;
};

export const searchMusicLibrary = async (query, provider = 'audius', limit = 10) => {
  const res = await axios.get(`${API_URL}/image-processing/music/search`, {
    ...header(),
    params: {
      q: query,
      provider,
      limit,
    },
  });
  return res.data;
};

export const uploadMusicAsset = async (file, metadata = {}) => {
  const formData = new FormData();
  formData.append('audio', file);

  if (metadata.title) {
    formData.append('title', metadata.title);
  }
  if (metadata.artist) {
    formData.append('artist', metadata.artist);
  }
  if (metadata.license_label) {
    formData.append('license_label', metadata.license_label);
  }
  if (metadata.license_url) {
    formData.append('license_url', metadata.license_url);
  }

  const res = await axios.post(
    `${API_URL}/image-processing/music/upload`,
    formData,
    header({ 'Content-Type': 'multipart/form-data' })
  );
  return res.data;
};

export const renderImageProcessingVideo = async (id, data) => {
  const res = await axios.post(`${API_URL}/image-processing/${id}/render-video`, data, header());
  return res.data;
};

export const clearImageProcessingVideo = async (id) => {
  const res = await axios.post(`${API_URL}/image-processing/${id}/clear-video`, {}, header());
  return res.data;
};

export const deleteImageProcessing = async (id) => {
  const res = await axios.delete(`${API_URL}/image-processing/${id}`, header());
  return res.data;
};

export const approveImageProcessing = async (id) => {
  const res = await axios.post(`${API_URL}/image-processing/${id}/approve`, {}, header());
  return res.data;
};

export const refuseImageProcessing = async (id) => {
  const res = await axios.post(`${API_URL}/image-processing/${id}/refuse`, {}, header());
  return res.data;
};

export default {
  listImageProcessing,
  getImageProcessing,
  createImageProcessing,
  updateImageProcessing,
  resetImageProcessingText,
  uploadProcessedImage,
  searchMusicLibrary,
  uploadMusicAsset,
  renderImageProcessingVideo,
  clearImageProcessingVideo,
  deleteImageProcessing,
  approveImageProcessing,
  refuseImageProcessing,
};

